import { useFirebase } from '@/services/analytics/use-firebase';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { analyticsService } from './firebase-analytics';
import { initInteractionTracking } from './bot-detection';
import { logDebug } from '@personal-portfolio/common-utils';
import { navigationItems } from '@/app/layout/navigation';

/** Stable analytics labels, deliberately decoupled from editorial copy. */
const PAGE_NAMES = {
  about: 'About',
  experience: 'Experience',
  projects: 'Projects',
  education: 'Education',
  certifications: 'Certifications',
  awards: 'Awards',
} as const;

export const FirebaseAnalyticsListener = () => {
  const { trackPageView } = useFirebase();
  const location = useLocation();
  const isInitializedRef = useRef<boolean | undefined>(undefined);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initializeFirebase = async () => {
      const isInitializeResult = await analyticsService.initialize();

      isInitializedRef.current = isInitializeResult;
      setIsInitialized(isInitializeResult);
    };

    if (isInitializedRef.current === undefined) {
      isInitializedRef.current = false;
      // Start interaction tracking at app start so the bot-detection
      // session clock (sessionStartTime) approximates page load rather
      // than the first tracked event.
      initInteractionTracking();
      initializeFirebase();
    }
  }, []);

  useEffect(() => {
    if (!isInitialized) {
      logDebug('Not initialized, skipping tracking');
      return;
    }

    if (location.pathname === '/') {
      trackPageView('home', 'Home');
      return;
    }

    const navItem = navigationItems.find(
      (item) => item.path === location.pathname,
    );
    if (navItem) {
      // Fixed names on purpose: analytics must stay comparable over time, so
      // page labels here never follow CMS copy edits.
      trackPageView(navItem.path.slice(1), PAGE_NAMES[navItem.titleKey]);
    } else {
      trackPageView(
        location.pathname.replace(/^\//, ''),
        document.title || 'Portfolio',
      );
    }
  }, [isInitialized, location.pathname, trackPageView]);

  return null;
};
